import type { UploadQueue } from './UploadQueue'
import type { ProcessableUpload, UploadPendingSendFileMeta, UploadPendingRetryUploadFile } from './Upload'
import type { ApiClient } from '../util/ApiClient'
import { sendFileMeta } from './sendFileMeta'
import { sendToS3 } from './sendToS3'
import type { ChunkReader } from './chunk'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'

export const processUpload = (
    apiClient: ApiClient,
    chunkReader: ChunkReader,
    upload: ProcessableUpload,
    uploadQueue: UploadQueue,
    logger: ClientLogger
) => {
    switch (upload.status) {
        case 'UploadPendingSendFileMeta':
            return sendMetaData(apiClient, chunkReader, upload, uploadQueue, logger)
        case 'UploadPendingRetryUploadFile':
            return uploadData(apiClient, chunkReader, upload, uploadQueue, logger)
        default:
            assumeExhaustion(upload)
    }
}

const sendMetaData = async (
    apiClient: ApiClient,
    chunkReader: ChunkReader,
    upload: UploadPendingSendFileMeta,
    uploadQueue: UploadQueue,
    logger: ClientLogger
) => {
    const { fileId } = upload.fileUploadData
    await uploadQueue.startingUploadFileMeta(fileId, logger)

    try {
        logger.log('Sending file metadata.', { fileId })
        await sendFileMeta(apiClient, upload.fileUploadData)
    } catch (e) {
        logger.error(e, { fileId })
        await uploadQueue.sendingFileMetaFailed(fileId, upload.failCount + 1, logger)
    }

    // Don't actually update the state to UploadPendingRetryUploadFile
    // as it could theoretically get picked up again by the upload manager
    // upload data will handle future state changes
    await uploadData(
        apiClient,
        chunkReader,
        {
            fileUploadData: upload.fileUploadData,
            groupId: upload.groupId,
            status: 'UploadPendingRetryUploadFile',
            failCount: 0,
            canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
        },
        uploadQueue,
        logger
    )
}

const uploadData = async (
    apiClient: ApiClient,
    chunkReader: ChunkReader,
    upload: UploadPendingRetryUploadFile,
    uploadQueue: UploadQueue,
    logger: ClientLogger
) => {
    const { fileId } = upload.fileUploadData
    await uploadQueue.startingFileUpload(fileId, logger)

    try {
        logger.log('Sending file to S3.', { fileId })
        await sendToS3({
            fileUploadData: upload.fileUploadData,
            reader: chunkReader,
            apiClient,
            onProgress: (bytesUploaded) =>
                uploadQueue.updateProgress(upload.fileUploadData.fileId, bytesUploaded, logger),
        })

        logger.log('File successfully sent to S3.', { fileId })
        await uploadQueue.uploadComplete(fileId, logger)
    } catch (e) {
        logger.error(e, { fileId })
        await uploadQueue.uploadFailed(fileId, upload.failCount + 1, logger)
    }
}
